import { type GradeCategoryWithLessons } from "@core/grades/types";
import { trans } from "@mongez/localization";
import {
  BookOpenIcon,
  FolderTreeIcon,
  GraduationCapIcon,
  SparklesIcon,
} from "lucide-react";
import { type ReactNode } from "react";

type CategoryDetailsHeroProps = {
  category: GradeCategoryWithLessons;
  childCategoriesCount: number;
  directLessonsCount: number;
  totalLessons: number;
};

export function CategoryDetailsHero({
  category,
  childCategoriesCount,
  directLessonsCount,
  totalLessons,
}: CategoryDetailsHeroProps) {
  return (
    <section className="shadow-primary-950/15 relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-indigo-950 via-primary-800 to-violet-700 px-5 py-7 text-white shadow-xl sm:px-8 sm:py-10 lg:px-12">
      <div className="pointer-events-none absolute -end-20 -top-24 h-64 w-64 rounded-full bg-fuchsia-400/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -start-12 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" />
      <div className="pointer-events-none absolute end-[12%] top-[18%] h-28 w-28 rotate-12 rounded-[2rem] border border-white/10 bg-white/5" />

      <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto]">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/90 backdrop-blur">
            <SparklesIcon className="h-3.5 w-3.5 text-amber-300" />
            {trans("categoryDetails.eyebrow")}
          </span>

          <div className="mt-5 flex items-start gap-4">
            <span className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur sm:flex">
              <FolderTreeIcon className="h-7 w-7" />
            </span>
            <div>
              <h1 className="text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
                {category.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-indigo-100 sm:text-base">
                {category.description ||
                  trans("categoryDetails.journeyDescription")}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 text-xs font-bold">
            {category.grade ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 backdrop-blur">
                <GraduationCapIcon className="h-4 w-4 text-cyan-200" />
                {category.grade.name}
              </span>
            ) : null}
            {category.subject ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 backdrop-blur">
                <BookOpenIcon className="h-4 w-4 text-amber-200" />
                {category.subject.name}
              </span>
            ) : null}
            {category.parent ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 backdrop-blur">
                <FolderTreeIcon className="h-4 w-4 text-fuchsia-200" />
                {category.parent.name}
              </span>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:w-[25rem]">
          <HeroMetric
            icon={<BookOpenIcon className="h-5 w-5" />}
            label={trans("categoryDetails.directLessons")}
            value={directLessonsCount}
          />
          <HeroMetric
            icon={<FolderTreeIcon className="h-5 w-5" />}
            label={trans("categoryDetails.childChapters")}
            value={childCategoriesCount}
          />
          <HeroMetric
            icon={<SparklesIcon className="h-5 w-5" />}
            label={trans("categoryDetails.totalLessons")}
            value={totalLessons}
          />
        </div>
      </div>
    </section>
  );
}

function HeroMetric({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 p-3 text-center backdrop-blur sm:p-4">
      <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-cyan-100">
        {icon}
      </span>
      <strong className="mt-2 block text-2xl font-black sm:text-3xl">
        {value}
      </strong>
      <span className="mt-1 block text-[10px] font-semibold leading-4 text-indigo-100 sm:text-xs">
        {label}
      </span>
    </div>
  );
}
